import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, XCircle, CircleNotch, Lightning } from "@phosphor-icons/react";

const MAX_POLLS = 12;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await refreshUser();
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        // ignore transient
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-forge-bg text-white flex items-center justify-center px-6 noise-overlay">
      <div className="relative z-10 border border-forge-border bg-forge-surface rounded-sm p-10 max-w-md w-full text-center" data-testid="payment-result">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Lightning size={20} weight="fill" className="text-forge-primary" />
          <span className="font-mono text-lg">Matrix</span>
        </div>

        {state === "checking" && (
          <>
            <CircleNotch size={44} className="text-forge-primary animate-spin mx-auto mb-5" />
            <h1 className="text-xl font-medium mb-2">Confirming your payment…</h1>
            <p className="text-sm text-neutral-400">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto mb-5" />
            <h1 className="text-xl font-medium mb-2">Credits added!</h1>
            <p className="text-sm text-neutral-400 mb-8">Your account has been updated. Time to build.</p>
            <button data-testid="go-dashboard" onClick={() => navigate("/")} className="bg-forge-primary text-black font-medium px-6 py-2.5 rounded-sm hover:bg-yellow-400 transition-colors">
              Back to dashboard
            </button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={48} weight="fill" className="text-red-500 mx-auto mb-5" />
            <h1 className="text-xl font-medium mb-2">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-sm text-neutral-400 mb-8">
              {state === "timeout"
                ? "Your payment is taking longer than usual. Credits will appear once it settles."
                : "Something went wrong. You were not charged."}
            </p>
            <button data-testid="go-billing" onClick={() => navigate("/billing")} className="border border-forge-border px-6 py-2.5 rounded-sm hover:bg-forge-hover transition-colors">
              Back to billing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
